import React from "react";
import { motion } from "framer-motion";
import {
    Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Sparkles, MessagesSquare, Library as LibraryIcon, Clock, Gauge, AlignLeft, GraduationCap } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const CONFETTI = Array.from({ length: 18 });
const COLORS = ["hsl(174,72%,46%)", "hsl(206,70%,52%)", "hsl(38,92%,62%)", "hsl(152,52%,46%)"];

const Confetti = () => (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {CONFETTI.map((_, i) => {
            const left = Math.random() * 100;
            const delay = Math.random() * 0.5;
            const dur = 1.6 + Math.random() * 1.2;
            const color = COLORS[i % COLORS.length];
            const size = 5 + Math.random() * 6;
            return (
                <motion.span
                    key={i}
                    initial={{ y: -30, x: 0, opacity: 0, rotate: 0 }}
                    animate={{ y: 420, opacity: [0, 1, 1, 0], rotate: 360 }}
                    transition={{ duration: dur, delay, ease: "easeIn" }}
                    style={{ position: "absolute", left: `${left}%`, top: 0, width: size, height: size, borderRadius: 2, background: color }}
                />
            );
        })}
    </div>
);

const Stat = ({ icon: Icon, label, value, suffix }) => (
    <div className="rounded-xl bg-surface-2 p-3 text-center">
        <Icon className="mx-auto h-4 w-4 text-[hsl(var(--primary))]" />
        <div className="mt-1.5 font-sans-display text-lg font-semibold text-t1">
            {value}{suffix && <span className="ml-0.5 text-xs font-normal text-t4">{suffix}</span>}
        </div>
        <div className="mono-label text-t4">{label}</div>
    </div>
);

export const CompletionDialog = ({ open, onOpenChange, title, stats, onReadSummary, onChat, onBackToLibrary, onStudy }) => {
    const { t } = useTranslation();
    const s = stats || {};
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="overflow-hidden border-border bg-surface-1 sm:max-w-md" data-testid="completion-dialog">
                {open && <Confetti />}
                <div className="relative flex flex-col items-center pt-2 text-center">
                    <motion.div
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 240, damping: 16 }}
                        className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                    >
                        <CheckCircle2 className="h-9 w-9" />
                    </motion.div>
                    <h2 className="mt-4 font-sans-display text-xl font-semibold text-t1">{t("completion.title")}</h2>
                    <p className="mt-1.5 max-w-xs text-sm text-t3">
                        {t("completion.body", { title })}
                        {s.timeSaved > 0 && <> {t("completion.faster", { min: s.timeSaved })}</>}
                    </p>

                    <div className="mt-5 grid w-full grid-cols-3 gap-2.5">
                        <Stat icon={AlignLeft} label={t("completion.words")} value={(s.totalWords || 0).toLocaleString()} />
                        <Stat icon={Gauge} label={t("completion.yourSpeed")} value={s.wpm || 0} suffix={t("completion.wpm")} />
                        <Stat icon={Clock} label={t("completion.timeSaved")} value={s.timeSaved || 0} suffix={t("completion.min")} />
                    </div>

                    <div className="mt-6 grid w-full grid-cols-1 gap-2">
                        <Button data-testid="completion-study-button" className="w-full justify-center gap-2" onClick={onStudy}>
                            <GraduationCap className="h-4 w-4" /> {t("completion.studyToRemember")}
                        </Button>
                        <Button data-testid="completion-summary-button" variant="secondary" className="w-full justify-center gap-2 bg-surface-3/60" onClick={onReadSummary}>
                            <Sparkles className="h-4 w-4" /> {t("completion.readSummary")}
                        </Button>
                        <div className="grid grid-cols-2 gap-2">
                            <Button data-testid="completion-chat-button" variant="secondary" className="justify-center gap-2 bg-surface-3/60" onClick={onChat}>
                                <MessagesSquare className="h-4 w-4" /> {t("completion.ask")}
                            </Button>
                            <Button data-testid="completion-library-button" variant="secondary" className="justify-center gap-2 bg-surface-3/60" onClick={onBackToLibrary}>
                                <LibraryIcon className="h-4 w-4" /> {t("completion.library")}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
